import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { z } from 'zod';
import { ISupplierCalendarQuestion } from '@bridebook/models/source/models/Suppliers/Calendars.types';
import { getQuestionAnswersShape } from '@bridebook/web/lib/showrounds/utils/form/question-answers-schema';
import { createPartnerNameSchema } from '@bridebook/web/lib/validation/partner-names/schema';

export const useShowroundFormSchema = (
  questions?: ISupplierCalendarQuestion[],
  isRequestMode?: boolean,
  isOpenDay?: boolean,
) => {
  const { t } = useTranslation('showrounds');

  return useMemo(() => {
    const phoneNumberSchema = z
      .object({
        nationalNumber: z.string(),
        full: z.string().min(1, t('validation.phoneNumber.required')),
        isValid: z.boolean(),
      })
      .refine((data) => data.isValid, {
        message: t('validation.phoneNumber.invalid'),
      });

    // Build the base schema
    const baseSchema: Record<string, z.ZodTypeAny> = {
      selectedDate:
        isRequestMode || isOpenDay
          ? z.any().optional()
          : z.any().refine((val) => val !== null && val !== undefined, {
              message: t('validation.date.required'),
            }),
      selectedSlot:
        isRequestMode || isOpenDay
          ? z.string().optional()
          : z.string().min(1, t('validation.timeSlot.required')),
      name: createPartnerNameSchema({
        allowEmpty: false,
        messages: {
          required: t('validation.name.required'),
        },
      }),
      partnerName: createPartnerNameSchema({
        allowEmpty: false,
        messages: {
          required: t('validation.partnerName.required'),
        },
      }),
      estimatedGuestCount: z.number().min(1, t('validation.guestCount.required')),
      phoneNumber: phoneNumberSchema,
      availabilityPreferences: isRequestMode
        ? z.array(z.string()).min(1, t('validation.availabilityPreferences.required'))
        : z.array(z.string()).optional(),
      ...getQuestionAnswersShape(questions ?? [], t),
    };

    const showroundFormSchema = z.object(baseSchema).passthrough();

    return { showroundFormSchema, phoneNumberSchema };
  }, [t, questions, isRequestMode, isOpenDay]);
};

export type ShowroundFormData = z.infer<
  ReturnType<typeof useShowroundFormSchema>['showroundFormSchema']
>;
